import React, { useContext } from 'react';
import styled from 'styled-components';
import { Link, useHistory } from 'react-router-dom';
import { Icon, Dropdown, Menu, MenuItem, Button } from 'components';
import { AuthContext, UniversalSearchContext } from 'contexts';
import { Button as ButtonV2, Icon as IconV2 } from '@brokerbay/elements-web';
import { Button as ButtonV3, Color } from '@bb/elements-web-rebranding';
import { featureEnabled } from 'utils';

type ListingActionsProps = {
  isDropdown?: boolean;
};

const ListingActions: React.FC<ListingActionsProps> = ({
  isDropdown = false,
}) => {
  const history = useHistory();
  const { auth, hasFeature, hasLimitedAccess, answeringPortalConfig } =
    useContext(AuthContext);
  const { setSearchOpen } = useContext(UniversalSearchContext);

  const isViewLiveConciergeAvailable =
    Boolean(auth?.roles) && answeringPortalConfig?.useLiveAppointmentDesk;

  const isSupraMvpInCanadaMlsStaffOrNotASupraMvp = Boolean(
    (hasFeature('SUPRA_MVP') &&
      (auth.org.country === 'CAN' || auth?.parentOrgType === 'mlsStaff')) ||
    !hasFeature('SUPRA_MVP'),
  );

  const isCreateListingAvailable = Boolean(
    hasFeature('CREATE_LISTING') && isSupraMvpInCanadaMlsStaffOrNotASupraMvp,
  );

  const isMergeCombineListingAvailable = Boolean(
    isSupraMvpInCanadaMlsStaffOrNotASupraMvp,
  );

  const canEditListing = featureEnabled('supraHideAdminFunctionalityFeature')
    ? isSupraMvpInCanadaMlsStaffOrNotASupraMvp
    : true;

  const hasSupraRebrandingFeature = featureEnabled('supraRebrandingFeature', 1);

  if (featureEnabled('showingsMadeEasyPhase2Feature')) {
    return (
      <MoreButtonContainer>
        {!isDropdown &&
          (hasSupraRebrandingFeature ? (
            <ButtonV3
              onClick={setSearchOpen}
              dataCy="dashboard-book-showing-button"
              size="small"
            >
              <IconV2 color="white" icon="calendarFilled" size={16} /> Book
              showing
            </ButtonV3>
          ) : (
            <ButtonV2
              onClick={setSearchOpen}
              dataCy="dashboard-book-showing-button"
              size="small"
            >
              <IconV2 color="white" icon="calendarFilled" size={16} /> Book
              showing
            </ButtonV2>
          ))}
        {(isViewLiveConciergeAvailable ||
          isCreateListingAvailable ||
          isMergeCombineListingAvailable) && (
          <Dropdown
            trigger={['click']}
            dropdownRender={() => (
              <Menu>
                {isViewLiveConciergeAvailable && (
                  <MenuItem
                    key={'view'}
                    data-cy="dashboard-live-concierge"
                    onClick={() =>
                      history.push(
                        auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN
                          ? '/admin/appointment_desk'
                          : '/profile?tab=LIVE_CONCIERGE_SERVICE',
                      )
                    }
                  >
                    View Live Concierge
                  </MenuItem>
                )}
                {hasFeature('CREATE_LISTING') && canEditListing && (
                  <MenuItem
                    key={'create'}
                    onClick={() => history.push('/listing/new')}
                    data-cy="dashboard-create-listing"
                    disabled={hasLimitedAccess}
                  >
                    Create Listing
                  </MenuItem>
                )}
                {canEditListing && (
                  <MenuItem
                    key={'merge-combine'}
                    onClick={() => history.push('/listing/merge/options')}
                    data-cy="dashboard-combine-listing"
                    disabled={hasLimitedAccess}
                  >
                    Merge/Combine Listing
                  </MenuItem>
                )}
              </Menu>
            )}
          >
            {isDropdown && canEditListing ? (
              <Button>
                Create Listing
                <Icon type="antdDownOutlined" />
              </Button>
            ) : (
              <ButtonV2
                type="secondary"
                size="small"
                dataCy="dashboard-more-button"
                styleOverride={{
                  minWidth: '32px',
                  ...(hasSupraRebrandingFeature && {
                    borderColor: Color.BORDER_GREY,
                  }),
                }}
              >
                <IconV2 icon="moreOutlined" size={16} color="primary" />
              </ButtonV2>
            )}
          </Dropdown>
        )}
      </MoreButtonContainer>
    );
  }

  if (
    isDropdown &&
    canEditListing &&
    (hasFeature('MY_BROKERAGE') || hasFeature('CREATE_LISTING'))
  ) {
    return (
      <div>
        <Dropdown
          dropdownRender={() => (
            <Menu>
              {auth?.roles && (
                <MenuItem
                  key={'view'}
                  data-cy="dashboard-live-concierge"
                  onClick={() =>
                    history.push(
                      auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN
                        ? '/admin/appointment_desk'
                        : '/profile?tab=LIVE_CONCIERGE_SERVICE',
                    )
                  }
                >
                  View Live Concierge
                </MenuItem>
              )}
              {hasFeature('CREATE_LISTING') && canEditListing && (
                <MenuItem
                  key={'create'}
                  onClick={() => history.push('/listing/new')}
                  data-cy="dashboard-create-listing"
                  disabled={hasLimitedAccess}
                >
                  Create Listing
                </MenuItem>
              )}
              {hasFeature('MY_BROKERAGE') && canEditListing && (
                <MenuItem
                  key={'merge-combine'}
                  onClick={() => history.push('/listing/merge/options')}
                  data-cy="dashboard-combine-listing"
                  disabled={hasLimitedAccess}
                >
                  Merge/Combine Listing
                </MenuItem>
              )}
            </Menu>
          )}
          disabled={hasLimitedAccess}
        >
          <Button>
            Create Listing
            <Icon type="antdDownOutlined" />
          </Button>
        </Dropdown>
      </div>
    );
  }
  return (
    <ActionButtonContainer>
      {auth?.roles && (
        <StyledActionButton>
          <Link
            data-cy="dashboard-view-live-concierge"
            to={
              auth?.roles[auth?.role] >= auth?.roles.GLOBAL_ADMIN
                ? '/admin/appointment_desk'
                : '/profile?tab=LIVE_CONCIERGE_SERVICE'
            }
          >
            View Live Concierge
          </Link>
        </StyledActionButton>
      )}
      {hasFeature('CREATE_LISTING') && canEditListing && (
        <StyledActionButton disabled={hasLimitedAccess}>
          <Link data-cy="dashboard-create-listing" to="/listing/new">
            Create Listing
          </Link>
        </StyledActionButton>
      )}
      {canEditListing && (
        <StyledActionButton disabled={hasLimitedAccess}>
          <Link data-cy="dashboard-combine-listing" to="/listing/merge/options">
            Merge/Combine Listing
          </Link>
        </StyledActionButton>
      )}
    </ActionButtonContainer>
  );
};

const MoreButtonContainer = styled.div`
  text-align: right;
  margin-left: auto;
  display: flex;
  gap: 8px;
`;

const ActionButtonContainer = styled.div`
  text-align: right;
  margin-left: auto;
`;

const StyledActionButton = styled(Button)`
  margin-left: 5px;
`;

export default ListingActions;
